import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { TrackEventTypes, TrackingType } from '@/entities/tracking/const';
import { BootstrapSignalKeys, BootstrapSignals } from '@/entities/tracking/lib/bootstrap-signals';
import { TrackerEngine } from '@/entities/tracking/lib/tracker-engine';
import {
    PAGE_UPDATE,
    ROUTE_CHANGE_COMPLETE,
    ROUTE_CHANGE_ERROR,
    ROUTE_CHANGE_START,
    UPDATE_DATA_LAYER,
} from '@/utils/constants/tracking';
import track from '@/utils/track';
import { isProfilePickerPathname } from '@/utils/tracking/isUserDataReadyForPageView';
import { resetOptimizelyExp } from '@/utils/tracking/resetOptimizelyExp';

let navigationSequence = 0;
// True while the current page's nav beacon is still waiting on INFO_UPDATE.
let hasPendingNavBeacon = false;

// Snapshot taken at route start so an error can tell an interrupted wait from a settled page.
let hadPendingBeaconAtRouteStart = false;
let beaconFiredBeforeNavigation = false;

export const getNavigationSequence = () => navigationSequence;

const isMfeHost = () => (globalThis as { isMfe?: boolean }).isMfe === true;

const handleRouteChangeStart = () => {
    hadPendingBeaconAtRouteStart = hasPendingNavBeacon;
    hasPendingNavBeacon = false;
    navigationSequence += 1;

    if (isMfeHost()) {
        // Revoke at route-start so PAGE_VIEW on the incoming page re-waits for
        // the fresh infoupdate emission from useTrackAppBaseSnapshot.
        BootstrapSignals.revoke(BootstrapSignalKeys.INFO_UPDATE);
        // Re-arm the pageview-emitted gate so the AOP navigation hold re-waits for the
        // destination page's PAGE_VIEW (marked in TrackingProvider) rather than the previous one.
        BootstrapSignals.revoke(BootstrapSignalKeys.PAGE_VIEW_EMITTED);
        // Re-arm too, or the destination's beacon treats the outgoing page's as its gate.
        // Remembered so an aborted navigation can restore it for the still-active page.
        if (BootstrapSignals.has(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED)) {
            beaconFiredBeforeNavigation = true;
        }
        BootstrapSignals.revoke(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED);
    }

    // Clear the accumulated optimizelyExp so the incoming page rebuilds it from
    // an empty baseline instead of inheriting the previous page's variants.
    // Both optimizelyExp writers union onto the existing snapshot value, so
    // without this reset variants leak forward across every client-side nav.
    void resetOptimizelyExp();
};

// revoke() leaves pending waiters queued, so a newer nav's mark would resolve this one too;
// hasPendingNavBeacon + the sequence guard decide which attempt's completion owns the beacon.
const armNavPageViewBeacon = (eventInfo: { to: string; from: string }) => {
    const navSequence = navigationSequence;
    hasPendingNavBeacon = true;
    const deferPickerPageView = isProfilePickerPathname(globalThis.location.pathname);
    BootstrapSignals.whenAll([BootstrapSignalKeys.INFO_UPDATE]).then(() => {
        if (navSequence !== navigationSequence) return;
        hasPendingNavBeacon = false;
        // @deprecated use the new TrackerEngine
        track(TrackEventTypes.NAV, PAGE_UPDATE, eventInfo, undefined, {
            skipPageView: deferPickerPageView,
        });
    });
};

// An aborted navigation ran the route-start reset with no routeChangeComplete
// rebuild, leaving the still-active page's tracking state empty and INFO_UPDATE
// revoked — rebuild here instead.
const handleRouteChangeError = () => {
    const abortedSequence = navigationSequence;
    const hadPendingBeacon = hadPendingBeaconAtRouteStart;

    // Deferred one task: a navigation that supersedes this one emits its
    // routeChangeStart in the same task as this error and must veto the rebuild
    // (its own completion rebuilds).
    setTimeout(() => {
        if (navigationSequence !== abortedSequence || !isMfeHost()) {
            return;
        }
        if (beaconFiredBeforeNavigation) {
            beaconFiredBeforeNavigation = false;
            BootstrapSignals.mark(BootstrapSignalKeys.PAGE_VIEW_BEACON_FIRED);
        }
        // Always restore INFO_UPDATE for the still-active page — routeChangeStart revoked it
        // unconditionally, so without this it stays revoked until some later nav completes.
        globalThis.dispatchEvent(new CustomEvent(`${UPDATE_DATA_LAYER}`));

        // Re-arm only if this page's own beacon was still pending (not already delivered)
        // when this failed attempt began — otherwise an unrelated nav error would duplicate it.
        if (!hadPendingBeacon || !document) return;
        armNavPageViewBeacon({ to: globalThis.location.href, from: document.referrer });
    }, 0);
};

const trackNavigation = () => {
    beaconFiredBeforeNavigation = false;

    // Dispatched before the beacon below so the destination's pageInfo is in the data layer
    // before it reads from there.
    if (isMfeHost()) {
        globalThis.dispatchEvent(new CustomEvent(`${UPDATE_DATA_LAYER}`));
    }

    if (!document) {
        return;
    }

    const eventInfo = {
        to: globalThis.location.href,
        from: document.referrer,
    };

    // migration to use the new TrackerEngine
    void TrackerEngine.update({
        type: TrackingType.NAV,
        detail: {
            context: eventInfo,
        },
    });

    armNavPageViewBeacon(eventInfo);
};

const useTrackNavigation = () => {
    const router = useRouter();

    useEffect(() => {
        router.events.on(ROUTE_CHANGE_START, handleRouteChangeStart);
        router.events.on(ROUTE_CHANGE_COMPLETE, trackNavigation);
        router.events.on(ROUTE_CHANGE_ERROR, handleRouteChangeError);

        return () => {
            router.events.off(ROUTE_CHANGE_START, handleRouteChangeStart);
            router.events.off(ROUTE_CHANGE_COMPLETE, trackNavigation);
            router.events.off(ROUTE_CHANGE_ERROR, handleRouteChangeError);
        };
    }, []);
};

export default useTrackNavigation;
